<!-- ActivityForm.vue -->
<script setup>
import { useRouter } from "vue-router";
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from "vue";

import { toTitleCase } from "@/utils/stringUtils";
import {
  PLANNED_PROGRESS_ACTION,
  ACTIVITY_PROGRESS_MENU_OPTIONS,
  progressShortLabel,
  progressFullLabel,
  progressColorClass,
  progressMenuColorClass,
  isSameProgress,
  activityPlanningProgress,
} from "@/utils/activityProgress";
import {
  formatDateTimeForDisplay,
  formatDateTimeForInput,
} from "@/utils/dateUtils";
import { storeToRefs } from "pinia";

import { useTextStore } from "@/stores/useTextStore";
import { useUserStore } from "@/stores/useUserStore";
import { useActivityStore } from "@/stores/useActivityStore";
// ── NOVO: guard de permissão por vínculo Membro-Titular ───────────────────────
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import { useMemberRestrictions } from "@/composables/useMemberRestrictions";

import AddActivityForm from "../Activity/AddActivityForm.vue";
import EditActivityForm from "../Activity/EditActivityForm.vue";
import ActivityCardModal from "../Activity/ActivityCardModal.vue";
import DeleteActivityForm from "../Activity/DeleteActivityForm.vue";
import BatchDeleteActivityForm from "../Activity/BatchDeleteActivityForm.vue";
import SearchActivityInput from "../Activity/SearchActivityInput.vue";
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";
import ActivityHelpModal from "./ActivityHelpModal.vue";
import MyActivitiesToggle from "../Activity/MyActivitiesToggle.vue";
import PermissionView from "../Generics/PermissionView.vue";

const showHelpModal = ref(false);

const props = defineProps({
  textId: { type: String, required: true },
  textTitle: { type: String, required: true },
});

// Stores
const textStore = useTextStore();
const userStore = useUserStore();
const activityStore = useActivityStore();
const router = useRouter();
// ── NOVO ──────────────────────────────────────────────────────────────────────
const managedAccountStore = useManagedAccountStore();

// ── NOVO: permissões efetivas do vínculo para atividades ─────────────────────
const memberActivityPermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.activities ?? null
);

const memberCanViewActivities = computed(
  () =>
    memberActivityPermissions.value === null ||
    memberActivityPermissions.value.view
);
// Criar/editar/excluir combinam o grid do vínculo com a opção "somente conteúdo
// do titular" (que bloqueia toda escrita de atividade pelo membro).
const {
  canCreateActivity: memberCanCreateActivity,
  canEditActivity: memberCanEditActivity,
  canDeleteActivity: memberCanDeleteActivity,
} = useMemberRestrictions();

// ── NOVO: guard de notas — portão para o botão "Anotar" na linha ──────────────
const memberNotePermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.notes ?? null
);
const memberCanViewNotes = computed(
  () => memberNotePermissions.value === null || memberNotePermissions.value.view
);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);
const isLoading = ref(true);

// Dropdowns
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ── Dropdown de progresso por linha ───────────────────────────────────────────
const openProgressDropdownId = ref(null);
const isProgressUpdating = ref(false);
const planningNow = ref(new Date());
let planningClockId = null;

const displayedProgress = (activity) =>
  activityPlanningProgress(activity, planningNow.value);

const displayedStartAt = (activity) =>
  activity.startedAt || activity.plannedStartAt || null;

const displayedEndAt = (activity) =>
  activity.endedAt || (!activity.startedAt ? activity.plannedEndAt : null);

const dateCellTitle = (activity, type) => {
  const isStart = type === "start";
  const value = isStart
    ? displayedStartAt(activity)
    : displayedEndAt(activity);
  if (!value) return isStart ? "Data de início não informada" : "Data de fim não informada";

  const isPlanned = !activity.startedAt &&
    (isStart ? Boolean(activity.plannedStartAt) : Boolean(activity.plannedEndAt));
  const label = isPlanned
    ? isStart ? "Início previsto" : "Fim previsto"
    : isStart ? "Início efetivo" : "Fim efetivo";
  return `${label}: ${formatDateTimeForDisplay(value)}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// LADO DE ABERTURA DOS MENUS DE LINHA
// ─────────────────────────────────────────────────────────────────────────────
// Os menus são position: absolute ancorados na célula — acompanham a rolagem e
// nunca se descolam da linha. O que falta é escolher o lado: aberto sempre para
// baixo, o menu de uma das últimas linhas passa da borda do .table-wrapper e só
// aparece depois de rolar. As classes opens-up/opens-left espelham a ancoragem.
const MENU_EDGE_MARGIN = 8;
const MENU_MIN_HEIGHT = 120;
const ROW_MENU_OFFSET = 42; // igual ao left: 42px de .row-dropdown-menu

const progressMenuPlacement = ref({ up: false, maxHeight: null, ready: false });
const rowMenuPlacement = ref({
  up: false,
  left: false,
  maxHeight: null,
  ready: false,
});

// Quem recorta é o .table-wrapper (overflow-x: auto faz o eixo Y computar auto).
// A janela entra na conta para o caso de o wrapper se estender além dela.
function getClipBounds(menuEl) {
  const wrapper = menuEl.closest(".table-wrapper");
  const r = wrapper?.getBoundingClientRect();
  return {
    top: Math.max(r?.top ?? 0, 0) + MENU_EDGE_MARGIN,
    bottom:
      Math.min(r?.bottom ?? window.innerHeight, window.innerHeight) -
      MENU_EDGE_MARGIN,
    left: Math.max(r?.left ?? 0, 0) + MENU_EDGE_MARGIN,
    right:
      Math.min(r?.right ?? window.innerWidth, window.innerWidth) -
      MENU_EDGE_MARGIN,
  };
}

// A altura real só existe depois do v-if renderizar o menu, daí o nextTick.
// Até a medição o menu fica com visibility: hidden, para não dar um salto
// visível quando a conta decidir abrir para o outro lado.
async function placeProgressMenu() {
  await nextTick();
  const menuEl = document.querySelector(".progress-dropdown-menu");
  const cell = menuEl?.closest(".progress-cell-container");
  if (!menuEl || !cell) return;

  const clip = getClipBounds(menuEl);
  const anchor = cell.getBoundingClientRect();
  const gap = 4;
  const h = menuEl.offsetHeight;

  const roomBelow = clip.bottom - anchor.bottom - gap;
  const roomAbove = anchor.top - clip.top - gap;
  const up = h > roomBelow && roomAbove > roomBelow;
  const room = up ? roomAbove : roomBelow;

  progressMenuPlacement.value = {
    up,
    // Não cabe em nenhum dos lados: limita e rola por dentro do menu.
    maxHeight: h > room ? Math.max(room, MENU_MIN_HEIGHT) : null,
    ready: true,
  };
}

async function placeRowMenu() {
  await nextTick();
  const menuEl = document.querySelector(".row-dropdown-menu");
  const cell = menuEl?.closest(".action-cell-container");
  if (!menuEl || !cell) return;

  const clip = getClipBounds(menuEl);
  const anchor = cell.getBoundingClientRect();
  const h = menuEl.offsetHeight;
  const w = menuEl.offsetWidth;

  // Vertical: alinhado pelo topo da célula; sem espaço, alinha pelo rodapé.
  const roomBelow = clip.bottom - anchor.top;
  const roomAbove = anchor.bottom - clip.top;
  const up = h > roomBelow && roomAbove > roomBelow;
  const room = up ? roomAbove : roomBelow;

  // Horizontal: à direita da célula; sem espaço, espelha para a esquerda.
  const fitsRight = anchor.left + ROW_MENU_OFFSET + w <= clip.right;
  const fitsLeft = anchor.right - ROW_MENU_OFFSET - w >= clip.left;

  rowMenuPlacement.value = {
    up,
    left: !fitsRight && fitsLeft,
    maxHeight: h > room ? Math.max(room, MENU_MIN_HEIGHT) : null,
    ready: true,
  };
}

// Vocabulário do progresso: PROGRESS_OPTIONS e rótulos vêm de
// @/utils/activityProgress, compartilhado com as demais telas de atividade.

// Modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const openEditInPlanningMode = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const showCardModal = ref(false);
const showPermissionViewModal = ref(false);

// Toast
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function goToTextForm() {
  const id = parseInt(props.textId);
  if (isNaN(id) || id <= 0) return;
  router.push({ name: "TextForm", params: { textId: id } });
}

// ── Fetch ─────────────────────────────────────────────────────────────────────
async function fetchActivitiesPaged() {
  isLoading.value = true;
  try {
    await activityStore.fetchActivitiesPaged(
      currentPage.value,
      pageSize.value,
      activityStore.searchTerm,
      props.textId
    );
  } catch (error) {
    console.error("❌ Erro ao buscar atividades paginadas:", error.message);
    showErrorToast("Falha ao carregar a lista de atividades.");
  } finally {
    isLoading.value = false;
  }
}

watch(currentPage, fetchActivitiesPaged);

// ── Seleção ───────────────────────────────────────────────────────────────────
const isAllSelectedOnPage = computed({
  get: () => {
    const editableIdsOnPage = activityStore.activities
      .filter((a) => canEdit(a))
      .map((a) => a.activityId);
    if (editableIdsOnPage.length === 0) return false;
    return editableIdsOnPage.every((id) =>
      activityStore.selectedActivityIds.includes(id)
    );
  },
  set: (value) => {
    const editableIdsOnPage = activityStore.activities
      .filter((a) => canEdit(a))
      .map((a) => a.activityId);
    if (value) {
      editableIdsOnPage.forEach((id) => {
        if (!activityStore.selectedActivityIds.includes(id)) {
          activityStore.selectedActivityIds.push(id);
        }
      });
    } else {
      activityStore.selectedActivityIds =
        activityStore.selectedActivityIds.filter(
          (id) => !editableIdsOnPage.includes(id)
        );
    }
  },
});

const anyEditableOnPage = computed(() =>
  activityStore.activities.some((a) => canEdit(a))
);

const canBatchActions = computed(
  () =>
    Array.isArray(activityStore.selectedActivityIds) &&
    activityStore.selectedActivityIds.length > 0
);

function toggleSelection(id) {
  activityStore.toggleSelection(id);
}

// ── Index selector ────────────────────────────────────────────────────────────
function indexSelector(index) {
  const activity = activityStore.activities[index];
  activityStore.setIdActivity(activity.activityId);
  activityStore.selectedActivity = activity;
}

// ── Row click ─────────────────────────────────────────────────────────────────
// O clique da linha leva a anotar, então carrega os guards que antes ficavam no
// botão: o portão do vínculo (memberCanViewNotes) e a permissão por atividade.
// Como uma linha não tem estado "desabilitado com tooltip", canOpenNotes também
// governa a classe .clickable-row — sem permissão, a linha nem exibe o cursor
// de mão, para o clique morto não parecer defeito.
function canOpenNotes(activity) {
  if (!memberCanViewNotes.value) return false;
  return (
    (hasAnyPermission(activity, "selecionar", "editar") && activity?.active) ||
    userStore.currentUser?.userId == activity?.createdByUserId
  );
}

function handleRowClick(index) {
  const activity = activityStore.activities[index];
  if (!canOpenNotes(activity)) return;
  indexSelector(index);
  goToRightArea(activity.activityId);
}

function handleChildNotify(payload) {
  if (!payload) return;
  if (payload.type === "error")
    showErrorToast(payload.message || "Ocorreu um erro.");
  else showSuccessToast(payload.message || "Operação realizada com sucesso.");
}

async function handleCardActivityUpdated() {
  await handleActivitiesChanged({
    goToFirstPage: true,
    closeAdd: false,
    closeEdit: false,
    closeDelete: false,
    closeBatchDelete: false,
    clearSelection: true,
  });
}

// ── Dropdowns ─────────────────────────────────────────────────────────────────
function toggleRowDropdown(activityId) {
  const willOpen = openDropdownId.value !== activityId;
  openDropdownId.value = willOpen ? activityId : null;
  // Um menu de linha por vez — placeRowMenu localiza o menu aberto pela classe.
  openProgressDropdownId.value = null;
  if (!willOpen) return;
  rowMenuPlacement.value = {
    up: false,
    left: false,
    maxHeight: null,
    ready: false,
  };
  placeRowMenu();
}

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
  if (
    openProgressDropdownId.value &&
    !event.target.closest(".progress-cell-container")
  ) {
    openProgressDropdownId.value = null;
  }
}

// ── Permissões de grupo ───────────────────────────────────────────────────────
function hasAnyPermission(entity, ...permissionNames) {
  if (!entity || !Array.isArray(entity.userPermissions)) return false;
  const lowerNames = permissionNames.map((p) => p.toLowerCase());
  return entity.userPermissions.some((perm) =>
    lowerNames.includes(perm.permissionName?.toLowerCase())
  );
}

// ── NOVO: guards combinam vínculo (portão) AND grupo (filtro interno) ─────────
function canEdit(activity) {
  return memberCanEditActivity.value && hasAnyPermission(activity, "Editar");
}
function canView(activity) {
  return (
    memberCanViewActivities.value && hasAnyPermission(activity, "Visualizar")
  );
}
function canSelect(activity) {
  return (
    memberCanViewActivities.value && hasAnyPermission(activity, "Selecionar")
  );
}
function isActivityCreator(activity) {
  return userStore.currentUser?.userId === activity?.createdByUserId;
}

// ── Navegação ─────────────────────────────────────────────────────────────────
async function goToRightArea(activityId) {
  try {
    await activityStore.openTextForAnnotation(props.textId, {
      preferActivityId: activityId,
    });

    router.push({ name: "RightArea" });
  } catch (error) {
    console.error("❌ Erro ao abrir a atividade para anotação:", error);
  }
}

// ── Ações ─────────────────────────────────────────────────────────────────────
function handleEdit(index) {
  indexSelector(index);
  openEditInPlanningMode.value = false;
  showEditModal.value = true;
  openDropdownId.value = null;
}

// Modo e status são excludentes: enquanto houver planejamento (futura, hoje ou
// atrasada) o status persistido — que segue "Em andamento" — não é o estado
// corrente da atividade, então só "Planejada" fica marcada.
function isProgressOptionCurrent(activity, option) {
  const planning = displayedProgress(activity);
  if (option.value === PLANNED_PROGRESS_ACTION.value) return Boolean(planning);
  return !planning && isSameProgress(activity.status, option.value);
}

function handleProgressOption(activity, option) {
  if (option.value !== PLANNED_PROGRESS_ACTION.value) {
    handleProgressChange(activity, option.value);
    return;
  }

  activityStore.setIdActivity(activity.activityId);
  activityStore.selectedActivity = activity;
  openProgressDropdownId.value = null;
  openEditInPlanningMode.value = true;
  showEditModal.value = true;
}

function handleView(index) {
  indexSelector(index);
  showCardModal.value = true;
  openDropdownId.value = null;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function handleViewPermissions(index) {
  indexSelector(index);
  showPermissionViewModal.value = true;
  openDropdownId.value = null;
}

function tryOpenBatchDeleteModal() {
  const selectedIds = activityStore.selectedActivityIds;
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
    showErrorToast("Nenhuma atividade selecionada.");
    isBatchDropdownOpen.value = false;
    return;
  }
  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// ── Handler único pós-mudança ─────────────────────────────────────────────────
async function handleActivitiesChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) activityStore.selectedActivityIds = [];

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchActivitiesPaged();
    return;
  }
  await fetchActivitiesPaged();
}

// ── Status toggle ─────────────────────────────────────────────────────────────
async function handleStatusToggle(activity) {
  try {
    await activityStore.updateActivityStatus({
      activityId: activity.activityId,
      active: !activity.active,
    });
    showSuccessToast(
      `Status da atividade "${toTitleCase(activity.title)}" alterado para ${
        activity.active ? "inativa" : "ativa"
      } com sucesso!`
    );
  } catch (error) {
    console.error("❌ Falha ao atualizar o status da atividade:", error);
    showErrorToast(
      error?.response?.data?.message ||
        error?.message ||
        "Ocorreu um erro ao atualizar o status."
    );
  }
}

// Progresso: a classe de cor do badge/das opções vem de progressColorClass, em
// @/utils/activityProgress, junto do restante do vocabulário.

// ── Progresso: dropdown inline por linha ──────────────────────────────────────
function toggleProgressDropdown(activityId, event) {
  event.stopPropagation();
  if (isProgressUpdating.value) return;
  const willOpen = openProgressDropdownId.value !== activityId;
  openProgressDropdownId.value = willOpen ? activityId : null;
  openDropdownId.value = null;
  if (!willOpen) return;
  progressMenuPlacement.value = { up: false, maxHeight: null, ready: false };
  placeProgressMenu();
}

async function handleProgressChange(activity, newStatus) {
  const planningState = displayedProgress(activity);
  if (isSameProgress(activity.status, newStatus) && !planningState) {
    openProgressDropdownId.value = null;
    return;
  }

  isProgressUpdating.value = true;
  openProgressDropdownId.value = null;
  try {
    // Busca o estado completo para não sobrescrever descrição/permissões no PUT.
    const full = await activityStore.getActivityById(activity.activityId);
    const rawPerms = await activityStore.fetchActivityPermissions(
      activity.activityId
    );
    const permissions = (rawPerms || []).map((p) => ({
      entityId: p.userId != null ? p.userId : p.groupId,
      permissionId: p.permissionId,
      type: p.userId != null ? "user" : "group",
    }));

    // "Concluída" exige Data de Fim: se ainda não houver, atribui a data/hora
    // atual no mesmo formato datetime-local usado no EditActivityForm.
    let endedAt = full.endedAt ?? null;
    if (newStatus === "Concluída" && !endedAt) {
      endedAt = formatDateTimeForInput(new Date());
    }

    await activityStore.updateActivity({
      activityId: full.activityId,
      title: full.title,
      description: full.description,
      status: newStatus,
      active: full.active,
      startedAt: planningState ? new Date().toISOString() : full.startedAt ?? null,
      endedAt,
      plannedStartAt: full.plannedStartAt ?? null,
      plannedEndAt: full.plannedEndAt ?? null,
      textId: full.textId,
      permissions,
    });

    await fetchActivitiesPaged();
    showSuccessToast(
      `Progresso da atividade "${toTitleCase(
        activity.title
      )}" alterado para "${progressFullLabel(newStatus)}" com sucesso!`
    );
  } catch (error) {
    console.error("❌ Falha ao atualizar o progresso da atividade:", error);
    showErrorToast(
      error?.response?.data?.message ||
        error?.message ||
        "Ocorreu um erro ao atualizar o progresso."
    );
  } finally {
    isProgressUpdating.value = false;
  }
}

const { activities } = storeToRefs(activityStore);

const isMyActivitiesActive = computed(() => activityStore.myActivitiesOnly);

function isSysUserName(name) {
  return (name ?? "").trim().toLowerCase() === "sys";
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  document.addEventListener("click", handleClickOutside);
  planningClockId = window.setInterval(() => {
    planningNow.value = new Date();
  }, 60000);
  const currentTextIdFromProp = parseInt(props.textId);
  if (!isNaN(currentTextIdFromProp) && currentTextIdFromProp > 0) {
    textStore.setIdText(currentTextIdFromProp);
    activityStore.setCurrentTextId(currentTextIdFromProp);
    await fetchActivitiesPaged();
  } else {
    console.error("ActivityForm mounted. Invalid textId prop:", props.textId);
    showErrorToast("TextId inválido para carregar atividades.");
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (planningClockId) window.clearInterval(planningClockId);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  activityStore.searchTerm = "";
  activityStore.currentPage = 1;
  activityStore.shouldForcePageOne = true;
  activityStore.myActivitiesOnly = false;
  activityStore.selectedActivityIds = [];
  openDropdownId.value = null;
  openProgressDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  showAddModal.value = false;
  showEditModal.value = false;
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  showCardModal.value = false;
  isLoading.value = false;
});
</script>

<template>
  <div class="w-full h-full">
    <div class="activity-container">
      <div class="activity-header">
        <div class="flex flex-col gap-0">
          <div class="flex items-center text-xl font-bold space-x-2">
            <button
              type="button"
              class="breadcrumb-link"
              title="Voltar para o texto"
              @click.stop="goToTextForm"
            >
              Textos
            </button>
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="4"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span>{{ props.textTitle }}</span>
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="4"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span>Atividades</span>
          </div>
          <div class="text-sm mt-1 text-gray-700 ">
            Gerencie atividades criadas por você ou compartilhadas com você.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2 flex items-center gap-2">
          <SearchActivityInput />
          <MyActivitiesToggle />
        </div>

        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <!-- ── NOVO: botão Adicionar condicionado ao guard de create ──────── -->
          <button
            v-if="memberCanCreateActivity"
            @click="showAddModal = true"
            class="btn btn-primary"
          >
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Atividade
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <ul class="p-0 m-0 list-none"></ul>
              <div class="py-1">
                <!-- ── NOVO: Apagar em lote condicionado ao guard de delete ── -->
                <a
                  v-if="memberCanDeleteActivity"
                  href="#"
                  role="button"
                  @click.prevent="tryOpenBatchDeleteModal"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="activity-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 10%">Ações</th>
              <th style="width: 6%">Id</th>
              <th style="width: 33%">Título</th>
              <th style="width: 12%">Progresso</th>
              <th
                style="width: 5%"
                title="Número de anotações ativas nesta atividade"
              >
                Notas
              </th>
              <th style="width: 13%">Data Início</th>
              <th style="width: 13%">Data Fim</th>
              <th style="width: 13%">Criador</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(activity, index) in !isLoading ? activities : []"
              :key="activity.activityId"
              @click="handleRowClick(index)"
              :class="{ 'clickable-row': canOpenNotes(activity) }"
              :title="
                canOpenNotes(activity)
                  ? null
                  : 'Sem permissão para anotar nesta atividade'
              "
            >
              <td @click.stop>
                <input
                  :class="{ 'disabled-item': !canEdit(activity) }"
                  type="checkbox"
                  :value="activity.activityId"
                  :checked="
                    activityStore.selectedActivityIds?.includes(
                      activity.activityId
                    )
                  "
                  @change="toggleSelection(activity.activityId)"
                  class="checkbox-style"
                  :disabled="!canEdit(activity)"
                />
              </td>

              <td class="action-cell-container" @click.stop>
                <div class="row-actions">
                  <button
                    v-if="
                      hasAnyPermission(activity, 'visualizar', 'selecionar')
                    "
                    @click="memberCanViewActivities && handleView(index)"
                    class="btn-icon btn-slate"
                    :class="{ 'btn-icon-disabled': !memberCanViewActivities }"
                    :title="
                      memberCanViewActivities
                        ? 'Detalhes da atividade'
                        : 'Sem permissão para visualizar atividades neste vínculo'
                    "
                    :aria-disabled="!memberCanViewActivities"
                  >
                    <i class="fas fa-info-circle fa-lg"></i>
                  </button>

                  <button
                    @click="toggleRowDropdown(activity.activityId)"
                    class="btn-icon btn-soft-brand"
                    title="Menu de Ações"
                  >
                    <i class="fas fa-ellipsis-h fa-lg"></i>
                  </button>
                </div>

                <div
                  v-if="openDropdownId === activity.activityId"
                  class="row-dropdown-menu"
                  :class="{
 'opens-up': rowMenuPlacement.up,
                    'opens-left': rowMenuPlacement.left,
                  }"
                  :style="{
                    maxHeight: rowMenuPlacement.maxHeight
                      ? rowMenuPlacement.maxHeight + 'px'
                      : null,
                    visibility: rowMenuPlacement.ready ? 'visible' : 'hidden',
                  }"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canEdit(activity) }"
                      @click="canEdit(activity) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li
                      :class="{
 'disabled-item':
                          !canView(activity) && !canSelect(activity),
                      }"
                      @click="
                        (canView(activity) || canSelect(activity)) &&
                          handleView(index)
                      "
                    >
                      <i class="fas fa-info-circle"></i
                      ><span>Detalhes da atividade</span>
                    </li>

                    <li
                      v-if="isActivityCreator(activity)"
                      @click="handleViewPermissions(index)"
                    >
                      <i class="fas fa-user-shield"></i
                      ><span>Ver permissões</span>
                    </li>

                    <!-- ── NOVO: Apagar condicionado ao guard de delete ────── -->
                    <li
                      v-if="memberCanDeleteActivity"
                      :class="{
 'disabled-item': !canEdit(activity),
                        'danger-item': true,
                      }"
                      @click="canEdit(activity) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>

                  <div class="dropdown-divider"></div>

                  <div class="dropdown-toggle-item">
                    <span class="label-text">Ativo</span>
                    <label class="toggle-switch">
                      <input
                        type="checkbox"
                        :checked="activity.active"
                        :disabled="!canEdit(activity)"
                        @change="handleStatusToggle(activity)"
                      />
                      <span class="slider"></span>
                    </label>
                  </div>
                </div>
              </td>

              <td>
                <span :title="activity.activityId" style="font-size: smaller">{{
                  activity.activityId
                }}</span>
              </td>

              <td class="cell-text-left">
                <span :title="activity.title">{{ activity.title }}</span>
              </td>

              <td @click.stop class="progress-cell-container">
                <button
                  class="progress-badge"
                  :class="
 displayedProgress(activity)?.colorClass ||
                    progressColorClass(activity.status)
                  "
                  @click="
                    canEdit(activity) &&
                      toggleProgressDropdown(activity.activityId, $event)
                  "
                  :disabled="isProgressUpdating || !canEdit(activity)"
                  :title="
                    canEdit(activity)
                      ? `${
                          displayedProgress(activity)?.label ||
                          progressFullLabel(activity.status)
                        } — clique para alterar o progresso`
                      : `${
                          displayedProgress(activity)?.label ||
                          progressFullLabel(activity.status)
                        } — sem permissão para editar esta atividade`
                  "
                >
                  <span class="progress-badge-text">{{
                    displayedProgress(activity)?.short ||
                    progressShortLabel(activity.status)
                  }}</span>
                  <i
                    v-if="canEdit(activity)"
                    class="fas fa-chevron-down progress-chevron"
                  ></i>
                </button>

                <div
                  v-if="openProgressDropdownId === activity.activityId"
                  class="progress-dropdown-menu"
                  :class="{ 'opens-up': progressMenuPlacement.up }"
                  :style="{
                    maxHeight: progressMenuPlacement.maxHeight
                      ? progressMenuPlacement.maxHeight + 'px'
                      : null,
                    visibility: progressMenuPlacement.ready
                      ? 'visible'
                      : 'hidden',
                  }"
                >
                  <ul>
                    <li
                      v-for="opt in ACTIVITY_PROGRESS_MENU_OPTIONS"
                      :key="opt.value"
                      :class="[
 'progress-option',
                        progressMenuColorClass(opt),
                        isProgressOptionCurrent(activity, opt)
                          ? 'progress-option--current'
                          : '',
                      ]"
                      @click="handleProgressOption(activity, opt)"
                      :title="opt.label"
                    >
                      <span class="progress-option-label">
                        <i
                          :class="[
 'fas',
                            opt.icon,
                            progressMenuColorClass(opt),
                            'progress-status-icon',
                            'progress-option-icon',
                          ]"
                        ></i>
                        {{ opt.short }}
                      </span>
                      <i
                        v-if="isProgressOptionCurrent(activity, opt)"
                        class="fas fa-check progress-check-icon"
                      ></i>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span
                  class="chips-note-count"
                  :class="{
 'active-notes':
                      (activity.noteCount || 0) + (activity.commentCount || 0) >
                      0,
                  }"
                  :title="`${
                    (activity.noteCount || 0) + (activity.commentCount || 0)
                  } notas e comentários ativos`"
                >
                  {{ (activity.noteCount || 0) + (activity.commentCount || 0) }}
                </span>
              </td>

              <td>
                <span
                  :class="[
 { 'planned-date': !activity.startedAt && activity.plannedStartAt },
                    !activity.startedAt && activity.plannedStartAt
                      ? displayedProgress(activity)?.colorClass
                      : '',
                  ]"
                  :title="dateCellTitle(activity, 'start')"
                  style="font-size: smaller"
                >
                  {{ formatDateTimeForDisplay(displayedStartAt(activity)) || "—" }}
                </span>
              </td>

              <td>
                <span
                  :class="[
 { 'planned-date': !activity.startedAt && activity.plannedEndAt },
                    !activity.startedAt && activity.plannedEndAt
                      ? displayedProgress(activity)?.colorClass
                      : '',
                  ]"
                  :title="dateCellTitle(activity, 'end')"
                  style="font-size: smaller"
                >
                  {{ formatDateTimeForDisplay(displayedEndAt(activity)) || "—" }}
                </span>
              </td>

              <td>
                <span
                  v-if="isSysUserName(activity.createdByUserName)"
                  class="sys-pill"
                  title="Criado pelo Sys"
                  >SYS</span
                >
                <span
                  v-else
                  :title="activity.createdByUserName"
                  style="font-size: smaller"
                >
                  {{ activity.createdByUserName }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoading">
              <td colspan="9" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando atividades...</p>
                </div>
              </td>
            </tr>

            <tr v-else-if="activities.length === 0">
              <td colspan="9" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <template v-if="isMyActivitiesActive">
                    <i class="fas fa-pen-to-square empty-icon empty-icon--invite"></i>
                    <p>Você ainda não tem atividades criadas.</p>
                    <p class="empty-hint">Clique no botão Adicionar Atividade para começar!</p>
                  </template>
                  <template v-else>
                    <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                    <p>Nenhuma atividade encontrada.</p>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoading && activityStore.totalCount > 0"
          class="activity-pagination-row"
        >
          <div class="activity-pagination-left">
            <span>Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="activityStore.totalCount"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="activityStore.totalCount"
            :total-pages="activityStore.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Modais -->
  <Teleport to="body">
    <AddActivityForm
      v-if="showAddModal && memberCanCreateActivity"
      @close="showAddModal = false"
      @refresh="
        handleActivitiesChanged({ goToFirstPage: true, closeAdd: true })
      "
    />

    <EditActivityForm
      v-if="showEditModal && memberCanEditActivity"
      :activityId="activityStore.idActivity"
      :textId="props.textId"
      :start-in-planning-mode="openEditInPlanningMode"
      @close="showEditModal = false; openEditInPlanningMode = false"
      @refresh="
        handleActivitiesChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <ActivityCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :activityId="activityStore.idActivity"
      @close="showCardModal = false"
      @activity-updated="handleCardActivityUpdated"
      @notify="handleChildNotify"
    />

    <ActivityHelpModal :show="showHelpModal" @close="showHelpModal = false" />

    <PermissionView
      v-if="showPermissionViewModal"
      :show="showPermissionViewModal"
      entityType="activity"
      :activityId="activityStore.idActivity"
      title="Permissões da atividade"
      @close="showPermissionViewModal = false"
    />

    <DeleteActivityForm
      v-if="showDeleteModal && memberCanDeleteActivity"
      :activity="activityStore.selectedActivity"
      @close="showDeleteModal = false"
      @refresh="
        handleActivitiesChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
      @notify="handleChildNotify"
    />

    <BatchDeleteActivityForm
      v-if="showBatchDeleteModal && memberCanDeleteActivity"
      @close="showBatchDeleteModal = false"
      @refresh="
        handleActivitiesChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
      @notify="handleChildNotify"
    />
  </Teleport>

  <!-- Toast -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.activity-container {
  background-color: var(--bg-content);
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-size: 0.95rem;
}

.activity-header {
  background: var(--bg-header);
  padding: 0.75rem 1.5rem;
  color: var(--text-dark);
}

.action-bar input,
.action-bar .search-input,
.search-text-input input {
  font-size: 0.8rem !important;
}

@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.btn-pastel-blue {
  background-color: var(--focus-border);
  color: #1c3d5a;
}
.btn-pastel-blue:hover {
  background-color: #82b4ff;
}
.btn-pastel-green {
  background-color: #9bf6ff;
  color: #1a4a4f;
}
/* Ação de consulta (card de detalhes): neutra, para não competir com a ação
   primária da linha. O ícone fica em tom médio de propósito — o disco sólido do
   info-circle pesa muito mais que os três pontos do menu de ações, e um slate
   escuro fazia o botão de consulta gritar mais alto que o de comandos. */
.btn-slate {
  background-color: var(--neutral-200);
  color: var(--text-muted);
}
.btn-slate:hover {
  background-color: var(--neutral-400);
}

.btn-icon-disabled {
  opacity: 0.4;
  cursor: not-allowed !important;
}
.btn-icon-disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}

/* Espelhos da ancoragem, ligados por placeRowMenu quando falta espaço */
.row-dropdown-menu.opens-up {
  top: auto;
  bottom: 0;
}
.row-dropdown-menu.opens-left {
  left: auto;
  right: 42px;
}

.row-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}

.dropdown-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 6px 0;
}
.dropdown-toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.65rem;
  cursor: default;
}
.label-text {
  font-size: 0.8rem;
  color: var(--text-dark);
}
.row-dropdown-menu .toggle-switch {
  width: 38px;
  height: 20px;
}
.row-dropdown-menu .slider {
  border-radius: 999px;
}
.row-dropdown-menu .slider:before {
  width: 14px;
  height: 14px;
  left: 3px;
  bottom: 3px;
}
.row-dropdown-menu input:checked + .slider:before {
  transform: translateX(18px);
}

.activity-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  text-align: center;
}
.activity-table th {
  padding: 0.6rem 0.5rem;
  text-transform: uppercase;
  font-size: 0.7rem;
  color: var(--text-dark);
  background: var(--bg-header);
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 10;
}
.activity-table tr {
  border-bottom: 1px solid var(--border-color);
}
.activity-table tbody tr:hover {
  background-color: var(--bg-active-nav);
}
.activity-table td {
  padding: 0.3rem 0.4rem;
  font-size: 0.8rem;
}
.activity-table .cell-text-left {
  text-align: left;
}
.activity-table td > span {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.planned-date {
  background: transparent;
  font-weight: 400;
}
.planned-date.status-future { background-color: transparent; color: #6d28d9; }
.planned-date.status-due-today { background-color: transparent; color: var(--link-hover); }
.planned-date.status-overdue { background-color: transparent; color: var(--danger-text); }

.sys-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.12rem 0.4rem;
  width: auto;
  white-space: nowrap;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(226, 232, 240, 0.8);
  color: rgba(51, 65, 85, 0.95);
}
td > .sys-pill {
  display: inline-flex !important;
}

/* ── Progresso: badge + dropdown por linha ──────────────────────────────── */
.progress-cell-container {
  position: relative;
}

.progress-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: filter 0.15s, transform 0.05s ease;
  /* Uma linha sempre: o rótulo curto cabe na coluna, e o ellipsis é a garantia
     final em telas muito estreitas (o nome completo fica no title) */
  max-width: 100%;
  white-space: nowrap;
}
.progress-badge-text {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.progress-chevron {
  flex-shrink: 0;
}
.progress-badge:hover:not(:disabled) {
  filter: brightness(0.93);
}
.progress-badge:active:not(:disabled) {
  transform: translateY(1px);
}
.progress-badge:focus,
.progress-badge:focus-visible {
  outline: none;
  box-shadow: none;
}
.progress-badge:disabled {
  cursor: not-allowed;
}
.progress-badge:disabled:hover {
  filter: none;
  transform: none;
}

.progress-chevron {
  font-size: 0.6rem;
  opacity: 0.7;
}

.progress-dropdown-menu {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: calc(100% + 4px);
  z-index: 100;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
  min-width: 175px;
  /* atende o max-height aplicado quando não cabe nem acima nem abaixo */
  overflow-y: auto;
}
/* Espelho da ancoragem, ligado por placeProgressMenu quando falta espaço abaixo */
.progress-dropdown-menu.opens-up {
  top: auto;
  bottom: calc(100% + 4px);
}
.progress-dropdown-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}
.progress-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.82rem;
  color: var(--text-dark);
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
/* Por padrão as opções ficam neutras; a cor do progresso (status-red/orange/...)
   aparece no hover e permanece sempre na opção atual. */
.progress-option:not(:hover):not(.progress-option--current) {
  background-color: transparent;
  color: var(--text-dark);
}
.progress-option:active {
  transform: translateY(1px);
}
.progress-option--current {
  font-weight: 700;
}
.progress-option-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}
.progress-option-icon {
  width: 0.95rem;
  text-align: center;
  font-size: 0.8rem;
}
.progress-status-icon {
  background-color: transparent !important;
}
.progress-check-icon {
  color: var(--brand-light);
  font-size: 0.75rem;
}

.status-red    { background-color: #ffc9c9; color: #c92a2a; }
.status-future { background-color: var(--brand-soft); color: #6d28d9; }
.status-due-today { background-color: var(--info-border); color: var(--link-hover); }
.status-overdue { background-color: var(--danger-bg); color: var(--danger-text); }
.status-orange { background-color: #ffd8a8; color: #e8590c; }
.status-blue   { background-color: #a5d8ff; color: #1971c2; }
.status-green  { background-color: #b2f2bb; color: #2b8a3e; }
.status-yellow { background-color: #fff3bf; color: #f59f00; }

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--neutral-200);
  transition: 0.4s;
  border-radius: 28px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 4px;
  bottom: 4px;
  background-color: var(--surface);
  transition: 0.4s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: var(--brand-light);
}
input:checked + .slider:before {
  transform: translateX(22px);
}
input:disabled + .slider {
  cursor: not-allowed;
  opacity: 0.5;
}

.chips-note-count {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-dark);
  background-color: var(--neutral-200);
  border: 1px solid #ced4da;
  transition: all 0.2s;
}
.chips-note-count.active-notes {
  background-color: #ccf7ff;
  color: #1a4a4f;
  border-color: #9bf6ff;
}

.breadcrumb-link {
  background: transparent;
  border: none;
  margin: 0;
  cursor: pointer;
  font-weight: inherit;
  font-size: inherit;
  color: inherit;
  text-decoration: none;
  transition: text-decoration 0.15s ease;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

.activity-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.87rem;
}
.activity-pagination-left {
  display: flex;
  align-items: center;
  gap: 4px;
}

.empty-state-content {
  width: 100%;
}

.empty-icon--invite {
  opacity: 0.75;
}
.empty-hint {
  font-size: 0.85rem;
  color: var(--text-muted);
}

</style>
